import { useEffect, useState } from "react";
import { toast } from "sonner";
import { partnerAdminApi } from "@/lib/partner";

const EMPTY_FORM = { name: "", description: "", url: "", mime_type: "", size_bytes: "" };

export default function PartnerMarketingAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await partnerAdminApi.listMarketingAssets();
      setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error("Name and URL required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        url: form.url.trim(),
      };
      if (form.mime_type.trim()) payload.mime_type = form.mime_type.trim();
      if (form.size_bytes.trim()) {
        const n = Number(form.size_bytes);
        if (Number.isFinite(n) && n > 0) payload.size_bytes = Math.floor(n);
      }
      await partnerAdminApi.addMarketingAsset(payload);
      toast.success("Asset added");
      setOpenAdd(false); setForm(EMPTY_FORM); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this asset? Partners will no longer see it.")) return;
    try {
      await partnerAdminApi.deleteMarketingAsset(id);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-partner-marketing">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/50">
          Downloadable marketing assets shown to active partners on /partner/marketing.
          Host the file wherever you like (Drive, S3, Notion) and paste the public URL here.
        </p>
        <button
          onClick={() => setOpenAdd(true)}
          data-testid="new-asset-btn"
          className="rounded-lg bg-[#D6A653] px-3 py-1.5 text-xs text-black"
        >
          Add asset
        </button>
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : items.length === 0 ? (
        <p className="text-white/40" data-testid="no-assets">No marketing assets yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((a) => (
            <div key={a.id} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4" data-testid={`asset-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white">{a.name}</p>
                  {a.description ? <p className="mt-0.5 truncate text-xs text-white/50">{a.description}</p> : null}
                  <a href={a.url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-[11px] text-[#D6A653] underline underline-offset-2">
                    {a.url}
                  </a>
                  <p className="mt-1 text-[11px] text-white/40">
                    {a.mime_type || "unknown mime"}{a.size_bytes ? ` · ${Math.round(a.size_bytes / 1024)} KB` : ""}
                  </p>
                </div>
                <button
                  onClick={() => remove(a.id)}
                  data-testid={`delete-asset-${a.id}`}
                  className="rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {openAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="add-asset-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <h3 className="text-lg font-light text-white">Add marketing asset</h3>
            <div className="mt-4 space-y-3">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="asset-name" />
              <Field label="Description (optional)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="asset-description" />
              <Field label="Public URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testId="asset-url" />
              <Field label="MIME type (optional)" value={form.mime_type} onChange={(v) => setForm({ ...form, mime_type: v })} testId="asset-mime" />
              <Field label="Size in bytes (optional)" value={form.size_bytes} onChange={(v) => setForm({ ...form, size_bytes: v })} testId="asset-size" />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setOpenAdd(false); setForm(EMPTY_FORM); }}
                      className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70">
                Cancel
              </button>
              <button onClick={add} disabled={busy} data-testid="asset-submit"
                      className="rounded-lg bg-[#D6A653] px-4 py-2 text-sm text-black disabled:opacity-60">
                {busy ? "Saving…" : "Add asset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider text-white/50">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
    />
  </label>
);
